var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useContext, useMemo, useRef, useState, } from 'react';
import { ThemeContext } from 'styled-components';
import { Container, Icon } from '../../../..';
import useOnClickOutside from '../../../../shared/hooks/useOnClickOutside';
import { TextSizeType } from '../../../../shared/styling/BaseText/BaseText.enum';
import { IconName } from '../../../Atoms/Icon/Icon.enum';
import Text from '../../../Atoms/Text/Text';
import BaseInputSelect from '../BaseComponents/BaseInputSelect';
import BaseSelectList from '../BaseComponents/BaseInputSelectList';
import BaseInputTextContainer from '../BaseComponents/BaseInputTextContainer';
import { createChoices, createClonesOfChildren, customizeInputSelectVisuals, getLabelFromValue, } from './InputSelect.helpers';
const InputSelect = forwardRef((_a, ref) => {
    var { value, placeholder, disabled, readOnly, listHeight = '400px', large, children, name, choices, touched, error, errorText, label, helperText, onChange, onBlur, customCss } = _a, delegated = __rest(_a, ["value", "placeholder", "disabled", "readOnly", "listHeight", "large", "children", "name", "choices", "touched", "error", "errorText", "label", "helperText", "onChange", "onBlur", "customCss"]);
    const [showDropdown, setShowDropdown] = useState(false);
    const placeholderValue = placeholder || 'Select option';
    const selectContainerRef = useRef(null);
    const theme = useContext(ThemeContext);
    const containerAndBaseInputCSS = customizeInputSelectVisuals();
    const choicesArray = choices
        ? createChoices(choices)
        : React.Children.toArray(children);
    const clickOutsideHandler = () => setShowDropdown(false);
    useOnClickOutside(selectContainerRef, clickOutsideHandler);
    const clickDropdownHandler = () => {
        if (disabled || readOnly)
            return;
        setShowDropdown(!showDropdown);
    };
    const onBlurHandler = (event) => {
        if (!showDropdown && onBlur) {
            event.persist();
            Object.defineProperty(event, 'target', {
                writable: true,
                value: { value: valueObj.value, name },
            });
            onBlur(event);
        }
    };
    const onChangeAutofillHandler = (event) => {
        const autofilled = choicesArray.find((child) => child.props.value === event.target.value);
        if (!autofilled)
            return;
        setValueObj({ value: autofilled.props.value, displayValue: autofilled.props.children });
        if (onChange) {
            onChange(event);
        }
    };
    const clickItemHandler = (child) => (event) => {
        const newValue = child.props.value;
        const newDisplyValue = child.props.children;
        const { inactive } = child.props;
        if (child.props.onClick) {
            child.props.onClick(event);
        }
        setValueObj({ value: newValue, displayValue: newDisplyValue });
        if (onChange) {
            Object.defineProperty(event, 'target', {
                writable: true,
                value: { value: newValue, name, inactive },
            });
            onChange(event);
        }
        setShowDropdown(false);
    };
    const clonedChildren = createClonesOfChildren(choicesArray, clickItemHandler);
    const controlledValue = useMemo(() => value ? { value, displayValue: getLabelFromValue(value, clonedChildren) || '' } : undefined, [clonedChildren, value]);
    const [valueObj, setValueObj] = useState({
        value: value || '',
        displayValue: getLabelFromValue(value, clonedChildren) || '',
    });
    return (_jsxs(Container, Object.assign({ ref: selectContainerRef, customCss: customCss, noPadding: true }, { children: [label && (_jsx(Text, Object.assign({ as: "label", htmlFor: name, textSize: TextSizeType.TEXT16 }, { children: label }), void 0)), _jsxs(Container, Object.assign({ customCss: containerAndBaseInputCSS, noPadding: true }, { children: [_jsxs(BaseInputTextContainer, Object.assign({ className: showDropdown ? 'active' : '', "data-testid": "select-button", disabled: disabled, error: touched && error, onBlur: onBlurHandler, onClick: clickDropdownHandler, readOnly: readOnly, role: "button", tabIndex: 0 }, { children: [_jsx(BaseInputSelect, Object.assign({ as: "div", "data-testid": "select-display", large: large }, { children: (controlledValue === null || controlledValue === void 0 ? void 0 : controlledValue.displayValue) || valueObj.displayValue || placeholderValue }), void 0), _jsx(Icon, { color: disabled ? theme.colors.disabled03 : theme.colors.interactivePrimary01, name: IconName.CHEVRON_DOWN }, void 0)] }), void 0), _jsx("input", Object.assign({ ref: ref, "aria-hidden": true, "data-testid": "select-input", disabled: disabled, id: name, name: name, onChange: onChangeAutofillHandler, style: { display: 'none' }, tabIndex: -1, value: (controlledValue === null || controlledValue === void 0 ? void 0 : controlledValue.value) || valueObj.value }, delegated), void 0), _jsx(BaseSelectList, Object.assign({ className: showDropdown ? 'visible' : '', "data-testid": "select-list", listHeight: listHeight }, { children: clonedChildren }), void 0)] }), void 0), helperText && (_jsx(Text, Object.assign({ color: theme.colors.text02, textSize: TextSizeType.TEXT14 }, { children: helperText }), void 0)), errorText && touched && error && (_jsx(Text, Object.assign({ color: theme.colors.supportError01, textSize: TextSizeType.TEXT14 }, { children: errorText }), void 0))] }), void 0));
});
export default InputSelect;
//# sourceMappingURL=InputSelect.js.map